// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { LogManager } from "@lib/logging/LogManager";
import { EventListener } from "@lib/utilities/EventListener";

const log = LogManager.createLog("CancellationToken");

/**
 * Propagates the cancellation of an operation.
 * Similar to .NETs `CancellationToken`.
 */
export class CancellationToken
{
  static combine(): never;
  static combine(...tokens: CancellationToken[]): CancellationToken;
  static combine(...tokens: CancellationToken[]): CancellationToken
  {
    if (tokens.length === 0)
      throw new Error("At least one cancellation token must be specified to combine.");

    return new CancellationToken(cancel =>
    {
      for (const token of tokens)
      {
        token.register(() => cancel());
      }
    });
  }

  private _onCanceled: Optional<EventListener<() => void>> = new EventListener();

  constructor(executor: (cancel: () => void) => void)
  {
    executor(() => this.cancel());
  }

  get isCanceled(): boolean
  {
    return this._onCanceled === undefined;
  }

  register(callback: () => void)
  {
    if (this._onCanceled)
    {
      this._onCanceled.addListener(callback);
    }
    else
    {
      try
      {
        callback();
      }
      catch (ex)
      {
        log.error("Event handler %o failed: %o", callback, ex);
      }
    }
  }

  private cancel = (): void =>
  {
    if (this._onCanceled)
    {
      this._onCanceled.dispatch();
      this._onCanceled.clearListeners();
      this._onCanceled = undefined;
    }
  }
}